import { useEffect, RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([disabled])',
].join(', ');

/**
 * Traps Tab / Shift+Tab focus cycling within the modal so keyboard users
 * cannot accidentally move focus to elements behind the overlay.
 * @param isOpen - Whether the modal is currently open
 * @param modalRef - Ref to the modal dialog element
 */
export function useModalFocusTrap(isOpen: boolean, modalRef: RefObject<HTMLDivElement>) {
  useEffect(() => {
    if (!isOpen) return;

    const modalElement = modalRef.current;
    if (!modalElement) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const candidates = modalElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const focusableElements = Array.from(candidates).filter(
        el => el.tabIndex >= 0 && (el as HTMLInputElement).type !== 'hidden',
      );

      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const isTabbable = Array.from(focusableElements).includes(
        document.activeElement as HTMLElement,
      );

      if (e.shiftKey) {
        if (!isTabbable || document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (!isTabbable || document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    modalElement.addEventListener('keydown', handleKeyDown);
    return () => modalElement.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, modalRef]);
}
